<script setup>
/**
 * Action bar — the controls belonging to a mode the reader can leave, such as a selection or an edit.
 *
 * They are held together as one band rather than scattered into the page's own controls.
 *
 * At a wide width it sits in the flow, directly above what it acts on. At a phone width it leaves
 * the flow and floats above the bottom edge, where a thumb already is: in the flow it would push
 * the content down by its own height the moment the mode began, and what is being acted on is what
 * the reader most needs to keep seeing.
 *
 * **Floating is opt-in through `standalone`, not automatic.** A bar pinned to the window from
 * inside a dialog escapes the surface that owns it and lands over whatever is behind. The bar
 * cannot see what it sits in, and it cannot ask — Quasar's breakpoints live in `primitives/` — so
 * the caller says, and the safe answer is the one you get by saying nothing.
 *
 * @status recommended
 * @group actions
 * @useWhen Holding the actions for a selection, or for any mode with a way out of it
 * @avoidWhen The controls are there whatever the reader is doing — that is a toolbar, not a mode
 * @related HrRecordsSection
 * @a11y Announced as a region; name it with an `aria-label` saying which mode it belongs to.
 */
defineOptions({ name: 'HrActionBar' });

defineProps({
  /**
   * This bar belongs to the screen rather than to something inside one, so at a phone width it may
   * anchor to the viewport.
   *
   * A dialog leaves it off. So does a bar inside a panel, a card, or anything else that owns a
   * surface of its own.
   */
  standalone: {
    type: Boolean,
    default: false,
  },
});
</script>

<template>
  <div
    role="region"
    :class="['hr-action-bar', standalone && 'hr-action-bar--standalone']"
  >
    <div
      v-if="$slots.leading"
      class="hr-action-bar__leading"
    >
      <!-- @slot What the mode is — the count of what is selected, and the way out of it. -->
      <slot name="leading" />
    </div>
    <div
      v-if="$slots.default"
      class="hr-action-bar__actions"
    >
      <!-- @slot What can be done from here, in the order the reader should meet them. -->
      <slot />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.hr-action-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--app-comp-action-bar-gap);
  min-height: var(--app-comp-action-bar-min-height);
}

// The state leads and the actions hold the far end, so the two read as cause and consequence.
.hr-action-bar__leading {
  display: flex;
  align-items: center;
  gap: var(--app-comp-action-bar-gap);
  min-width: 0;
}

.hr-action-bar__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--app-comp-action-bar-gap);
  margin-inline-start: auto;
}

/*
 * Floating is the phone-width form, and only for a bar that belongs to the screen. It takes a
 * surface and an elevation here that it does not need in the flow, because off the flow it has to
 * say where it ends.
 */
@media (max-width: 599px) {
  .hr-action-bar--standalone {
    position: fixed;
    z-index: var(--app-z-floating);
    inset-inline: var(--app-comp-action-bar-floating-inset);
    inset-block-end: var(--app-comp-action-bar-floating-inset);
    padding: var(--app-comp-action-bar-floating-padding);
    border-radius: var(--app-shape-corner-large);
    background: var(--app-surface-container-high);
    color: var(--app-on-surface);
    box-shadow: var(--app-elevation-3);
  }
}
</style>
